import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Tooltip,
  InputAdornment, Collapse, Tab, Tabs, Snackbar,
} from '@mui/material';
import {
  Search, Refresh, PictureAsPdf, GridOn, Print, North, South, ExpandMore, ExpandLess,
  OpenInNew, AccountBalance, TrendingFlat, WarningAmberRounded, Lightbulb,
  FilterList, ArrowForward,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import { financeService } from '../../services/financeService';
import { EnhancedGeneralLedger, LedgerEntry } from '../../types/finance';
import { formatCurrency, formatAmount, formatIndianNumber } from '../../utils/currency';
import { exportToPDF, exportToExcel, formatIndianCurrency } from '../../utils/exportUtils';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import AccountSelect from '../../components/finance/AccountSelect';
import { StatusDot, StatusTone } from '../../components/common/pageKit';

const JOURNAL_TYPES = ['General Journal', 'Adjustment', 'Accrual', 'Provision', 'Bank', 'Cash', 'Treasury', 'Purchase', 'Sales', 'Payroll', 'GST', 'TDS', 'Inventory', 'Production', 'Depreciation', 'Asset', 'Intercompany', 'Reversal'];

// ---- period presets (Indian FY Apr–Mar) -----------------------------------
type Preset = 'today' | 'thisMonth' | 'prevMonth' | 'thisQuarter' | 'thisFY' | 'prevFY' | 'last30' | 'last90' | 'custom';
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fyStart = (d: Date) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);
const presetRange = (p: Preset, today = new Date()): { from: string; to: string } => {
  const y = today.getFullYear(), m = today.getMonth();
  switch (p) {
    case 'today': return { from: iso(today), to: iso(today) };
    case 'thisMonth': return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
    case 'prevMonth': return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'thisQuarter': { const q = Math.floor(m / 3); return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) }; }
    case 'prevFY': { const s = fyStart(today) - 1; return { from: `${s}-04-01`, to: `${s + 1}-03-31` }; }
    case 'last30': return { from: iso(new Date(y, m, today.getDate() - 30)), to: iso(today) };
    case 'last90': return { from: iso(new Date(y, m, today.getDate() - 90)), to: iso(today) };
    case 'thisFY': default: { const s = fyStart(today); return { from: `${s}-04-01`, to: `${s + 1}-03-31` }; }
  }
};
const PRESETS: { key: Preset; label: string }[] = [
  { key: 'thisFY', label: 'This FY' }, { key: 'prevFY', label: 'Prev FY' },
  { key: 'thisQuarter', label: 'This Quarter' }, { key: 'thisMonth', label: 'This Month' },
  { key: 'prevMonth', label: 'Prev Month' }, { key: 'last30', label: 'Last 30d' },
  { key: 'last90', label: 'Last 90d' }, { key: 'today', label: 'Today' },
];

const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const monthLabel = (m: string) => { const [y, mo] = m.split('-'); return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-IN', { month: 'short' }); };
const typeTint = (t: string | null | undefined, accent: string) => {
  const map: Record<string, string> = { Reversal: '#a855f7', Depreciation: '#0891b2', Sales: '#0284c7', Purchase: '#059669', Payroll: '#7c3aed', GST: '#d97706', TDS: '#d97706', Treasury: '#0284c7' };
  return (t && map[t]) || accent;
};

const Bal: React.FC<{ amount: number; type: 'Dr' | 'Cr'; bold?: boolean; size?: string }> = ({ amount, type, bold, size }) => (
  <Typography component="span" sx={{ fontWeight: bold ? 800 : 600, fontVariantNumeric: 'tabular-nums', fontSize: size, whiteSpace: 'nowrap' }}>
    {formatCurrency(amount)} <Typography component="span" variant="caption" sx={{ color: type === 'Dr' ? '#0284c7' : '#7c3aed', fontWeight: 700 }}>{type}</Typography>
  </Typography>
);

const GeneralLedgerWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountId, setAccountId] = useState(searchParams.get('account') || '');
  const [preset, setPreset] = useState<Preset>('thisFY');
  const init = presetRange('thisFY');
  const [fromDate, setFromDate] = useState(init.from);
  const [toDate, setToDate] = useState(init.to);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fType, setFType] = useState('');
  const [fSource, setFSource] = useState('');
  const [fMin, setFMin] = useState('');
  const [fMax, setFMax] = useState('');
  const [onlyReversed, setOnlyReversed] = useState(false);

  const [data, setData] = useState<EnhancedGeneralLedger | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    financeService.getAccounts().then((r: any) => setAccounts(r.accounts || [])).catch(() => {});
  }, []);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') { const r = presetRange(p); setFromDate(r.from); setToDate(r.to); }
  };

  const load = useCallback(() => {
    if (!accountId) { setData(null); return; }
    setLoading(true); setError(null);
    financeService.getEnhancedGeneralLedger(Number(accountId), fromDate, toDate, committedSearch || undefined)
      .then((d) => setData(d))
      .catch((e) => { setError(e.message || 'Failed to load ledger'); setData(null); })
      .finally(() => setLoading(false));
  }, [accountId, fromDate, toDate, committedSearch]);

  useEffect(() => { load(); }, [load]);

  const acc = data?.account;

  // ---- client-side advanced filtering (server already scoped by acct/date/search)
  const rows: LedgerEntry[] = useMemo(() => {
    let r = data?.entries || [];
    if (fType) r = r.filter((e) => e.journal_type === fType);
    if (fSource) r = r.filter((e) => (e.source_type || 'MANUAL') === fSource);
    if (onlyReversed) r = r.filter((e) => !!e.reversal_entry_id);
    if (fMin) r = r.filter((e) => Math.max(e.debit_amount, e.credit_amount) >= Number(fMin));
    if (fMax) r = r.filter((e) => Math.max(e.debit_amount, e.credit_amount) <= Number(fMax));
    return r;
  }, [data, fType, fSource, onlyReversed, fMin, fMax]);

  const filtered = fType || fSource || onlyReversed || fMin || fMax;

  const sourceTypes = useMemo(() => Array.from(new Set((data?.entries || []).map((e) => e.source_type || 'MANUAL'))), [data]);

  // ---- deterministic insights + exceptions ----------------------------------
  const insights = useMemo(() => {
    if (!data || !acc) return [];
    const list: string[] = [];
    const n = data.entries.length;
    list.push(`${n} transaction${n === 1 ? '' : 's'} posted in the selected period.`);
    if (n && data.entries.every((e) => e.status === 'POSTED')) list.push('All transactions in this period are posted.');
    if (data.lastTransactionDate) list.push(`Last transaction was posted on ${fmtDate(data.lastTransactionDate)}.`);
    list.push(`This account currently has a ${data.closingBalance.type === 'Dr' ? 'debit' : 'credit'} balance of ${formatCurrency(data.closingBalance.amount)}.`);
    if (n) { const avg = (data.totals.totalDebit + data.totals.totalCredit) / n; list.push(`Average transaction value is ${formatCurrency(avg)}.`); }
    return list;
  }, [data, acc]);

  const exceptions = useMemo(() => {
    if (!data) return [] as { title: string; impact: string; tone: StatusTone }[];
    const out: { title: string; impact: string; tone: StatusTone }[] = [];
    const rev = data.entries.filter((e) => e.reversal_entry_id);
    if (rev.length) out.push({ title: `${rev.length} reversed entr${rev.length === 1 ? 'y' : 'ies'} in period`, impact: 'Original and counter-entry both remain in the ledger and net to zero.', tone: 'neutral' });
    const amts = data.entries.map((e) => Math.max(e.debit_amount, e.credit_amount));
    if (amts.length >= 3) {
      const mean = amts.reduce((s, x) => s + x, 0) / amts.length;
      const sd = Math.sqrt(amts.reduce((s, x) => s + (x - mean) ** 2, 0) / amts.length);
      const big = data.entries.filter((e) => Math.max(e.debit_amount, e.credit_amount) > mean + 2 * sd);
      if (big.length) out.push({ title: `${big.length} unusually large transaction${big.length === 1 ? '' : 's'} detected`, impact: `Above ${formatCurrency(mean + 2 * sd)} (2σ over the period mean). Review for correctness.`, tone: 'warning' });
    }
    const noRef = data.entries.filter((e) => (!e.source_type || e.source_type === 'MANUAL') && !e.reference);
    if (noRef.length) out.push({ title: `${noRef.length} manual entr${noRef.length === 1 ? 'y' : 'ies'} without a reference`, impact: 'Manual journals with no supporting reference are harder to audit.', tone: 'warning' });
    return out;
  }, [data]);

  // ---- exports --------------------------------------------------------------
  const exportPDF = () => { if (data) exportToPDF('gl-report', `GL_${acc?.account_code}_${fromDate}_${toDate}`, 'landscape'); };
  const exportExcel = () => {
    if (!data) return;
    const rowsX: Record<string, unknown>[] = [{ date: 'Opening Balance', voucher: '', type: '', narration: '', debit: '', credit: '', balance: `${formatIndianCurrency(data.openingBalance.amount)} ${data.openingBalance.type}` }];
    rows.forEach((e) => rowsX.push({ date: fmtDate(e.entry_date), voucher: e.entry_number, type: e.journal_type || '', narration: e.reference ? `${e.description} (${e.reference})` : e.description, debit: e.debit_amount > 0 ? formatIndianCurrency(e.debit_amount) : '', credit: e.credit_amount > 0 ? formatIndianCurrency(e.credit_amount) : '', balance: `${formatIndianCurrency(e.running_balance)} ${e.balance_type}` }));
    rowsX.push({ date: 'Closing Balance', voucher: '', type: '', narration: '', debit: formatIndianCurrency(data.totals.totalDebit), credit: formatIndianCurrency(data.totals.totalCredit), balance: `${formatIndianCurrency(data.closingBalance.amount)} ${data.closingBalance.type}` });
    exportToExcel(rowsX, [{ header: 'Date', key: 'date' }, { header: 'Voucher', key: 'voucher' }, { header: 'Journal Type', key: 'type' }, { header: 'Narration', key: 'narration' }, { header: 'Debit', key: 'debit' }, { header: 'Credit', key: 'credit' }, { header: 'Balance', key: 'balance' }], `GL_${acc?.account_code}_${fromDate}_${toDate}`, 'General Ledger');
  };

  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;
  const labelSx = { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' as const, color: c.sub };

  const goVoucher = (e: LedgerEntry) => { const id = e.journal_entry_id ?? e.id; if (id) navigate(`/finance/journal/${id}`); };

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      {/* header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>General Ledger</Typography>
          <Typography variant="body2" color="text.secondary">Account-wise transaction activity, balances and financial movement.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={!accountId || loading} sx={{ border: line }}><Refresh fontSize="small" /></IconButton></span></Tooltip>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdf />} disabled={!data} onClick={exportPDF}>PDF</Button>
          <Button variant="outlined" size="small" startIcon={<GridOn />} disabled={!data} onClick={exportExcel}>Excel</Button>
          <Button variant="outlined" size="small" startIcon={<Print />} disabled={!data} onClick={() => window.print()}>Print</Button>
        </Stack>
      </Stack>

      {/* account + period control bar */}
      <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 2.5 }}>
        <Grid container spacing={1.5} alignItems="flex-end">
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Account *</Typography>
            <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} placeholder="Search account by code or name…" />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Period</Typography>
            <TextField select fullWidth size="small" value={preset} onChange={(e) => applyPreset(e.target.value as Preset)}>
              {PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>From</Typography>
            <TextField type="date" fullWidth size="small" value={fromDate} InputLabelProps={{ shrink: true }} onChange={(e) => { setFromDate(e.target.value); setPreset('custom'); }} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>To</Typography>
            <TextField type="date" fullWidth size="small" value={toDate} InputLabelProps={{ shrink: true }} onChange={(e) => { setToDate(e.target.value); setPreset('custom'); }} />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.5 }}>Search</Typography>
            <TextField fullWidth size="small" placeholder="Narration, voucher, ref" value={search}
              onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setCommittedSearch(search)}
              onBlur={() => setCommittedSearch(search)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 17, color: c.sub }} /></InputAdornment> }} />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          {PRESETS.slice(0, 6).map((p) => (
            <Chip key={p.key} label={p.label} size="small" clickable variant={preset === p.key ? 'filled' : 'outlined'}
              color={preset === p.key ? 'primary' : 'default'} onClick={() => applyPreset(p.key)} sx={{ borderColor: c.line }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<FilterList />} onClick={() => setShowAdvanced((s) => !s)}>Advanced Filters</Button>
        </Stack>
        <Collapse in={showAdvanced}>
          <Divider sx={{ my: 1.5, borderColor: c.line }} />
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField select fullWidth size="small" label="Journal Type" value={fType} onChange={(e) => setFType(e.target.value)}>
                <MenuItem value="">All types</MenuItem>
                {JOURNAL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField select fullWidth size="small" label="Source" value={fSource} onChange={(e) => setFSource(e.target.value)}>
                <MenuItem value="">All sources</MenuItem>
                {sourceTypes.map((s) => <MenuItem key={s} value={s}>{String(s).replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" type="number" label="Min ₹" value={fMin} onChange={(e) => setFMin(e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" type="number" label="Max ₹" value={fMax} onChange={(e) => setFMax(e.target.value)} /></Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Chip label="Reversed only" size="small" clickable variant={onlyReversed ? 'filled' : 'outlined'} color={onlyReversed ? 'secondary' : 'default'} onClick={() => setOnlyReversed((v) => !v)} sx={{ borderColor: c.line, mt: 1 }} />
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {error && <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 2, color: c.bad }}>{error}</Paper>}

      {!accountId ? (
        <Paper elevation={0} sx={{ ...panelSx, py: 8, textAlign: 'center' }}>
          <AccountBalance sx={{ fontSize: 44, color: c.sub, mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 700 }}>Select an account</Typography>
          <Typography variant="body2" color="text.secondary">Choose an account above to view its ledger, balances and movement.</Typography>
        </Paper>
      ) : loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : data && acc ? (
        <Box id="gl-report">
          {/* KPI strip */}
          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            {[
              { t: 'Opening Balance', node: <Bal amount={data.openingBalance.amount} type={data.openingBalance.type} bold size="1.35rem" />, tint: '#0284c7' },
              { t: 'Total Debit', node: <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#059669', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(data.totals.totalDebit)}</Typography>, tint: '#059669' },
              { t: 'Total Credit', node: <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(data.totals.totalCredit)}</Typography>, tint: '#dc2626' },
              { t: 'Net Movement', node: <Bal amount={data.netMovement ?? 0} type={(data.netMovementType as 'Dr' | 'Cr') || 'Dr'} bold size="1.35rem" />, tint: '#7c3aed' },
              { t: 'Closing Balance', node: <Bal amount={data.closingBalance.amount} type={data.closingBalance.type} bold size="1.35rem" />, tint: '#4f46e5' },
              { t: 'Transactions', node: <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#ea580c' }}>{data.count}</Typography>, tint: '#ea580c' },
            ].map((k) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.t}>
                <Paper elevation={0} sx={{ ...panelSx, p: 1.75, height: '100%', background: `linear-gradient(160deg, ${k.tint}0d 0%, transparent 70%)` }}>
                  <Typography sx={{ ...labelSx, mb: 0.5 }}>{k.t}</Typography>
                  {k.node}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* account context + movement */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: `${c.accent}14` }}>
                    <AccountBalance sx={{ color: c.accent }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      <Box component="span" sx={{ fontFamily: 'monospace', color: c.accent, mr: 1 }}>{acc.account_code}</Box>{acc.account_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{acc.parentName ? `Under ${acc.parentName}` : 'Top-level account'}</Typography>
                  </Box>
                </Stack>
                <Grid container spacing={1.5}>
                  <Grid size={6}><Typography sx={labelSx}>Account Type</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{acc.account_type}</Typography></Grid>
                  <Grid size={6}><Typography sx={labelSx}>Normal Balance</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{acc.normalBalance === 'Dr' ? 'Debit' : 'Credit'}</Typography></Grid>
                  <Grid size={6}><Typography sx={labelSx}>Status</Typography><StatusDot label={acc.isActive === false ? 'Inactive' : 'Active'} tone={acc.isActive === false ? 'neutral' : 'success'} /></Grid>
                  <Grid size={6}><Typography sx={labelSx}>Currency</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{acc.currency || 'INR'}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1.5 }}>Account Movement</Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                  <MoveNode c={c} label="Opening" node={<Bal amount={data.openingBalance.amount} type={data.openingBalance.type} bold />} />
                  <TrendingFlat sx={{ color: c.sub }} />
                  <MoveNode c={c} label="+ Debits" node={<Typography sx={{ fontWeight: 800, color: '#059669' }}>{formatIndianNumber(data.totals.totalDebit)}</Typography>} />
                  <TrendingFlat sx={{ color: c.sub }} />
                  <MoveNode c={c} label="− Credits" node={<Typography sx={{ fontWeight: 800, color: '#dc2626' }}>{formatIndianNumber(data.totals.totalCredit)}</Typography>} />
                  <TrendingFlat sx={{ color: c.sub }} />
                  <MoveNode c={c} label="Closing" node={<Bal amount={data.closingBalance.amount} type={data.closingBalance.type} bold />} highlight />
                </Stack>
                {(data.monthly && data.monthly.length > 0) && (
                  <Box sx={{ height: 150, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.monthly} margin={{ top: 4, right: 6, bottom: 0, left: -14 }} barGap={2}>
                        <CartesianGrid {...gridProps(ct)} />
                        <XAxis dataKey="month" tickFormatter={monthLabel} {...axisProps(ct)} />
                        <YAxis {...axisProps(ct)} width={40} tickFormatter={(v) => formatIndianNumber(v).replace('₹', '')} />
                        <RTooltip {...tooltipStyle(ct)} formatter={(v: any, n: any) => [formatCurrency(Number(v)), n === 'debit' ? 'Debit' : 'Credit']} labelFormatter={monthLabel} />
                        <Bar dataKey="debit" fill="#059669" radius={[3, 3, 0, 0]} maxBarSize={26} />
                        <Bar dataKey="credit" fill="#dc2626" radius={[3, 3, 0, 0]} maxBarSize={26} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* tabs */}
          <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: line, px: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}>
              <Tab label="Transactions" />
              <Tab label="Account Summary" />
              <Tab label="Analysis" />
              <Tab label="Insights & Exceptions" />
            </Tabs>

            {/* --- transactions --- */}
            {tab === 0 && (
              <TableContainer sx={{ maxHeight: 620 }}>
                <Table size="small" stickyHeader sx={{ '& td, & th': { borderColor: c.line } }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, bgcolor: c.panel2 } }}>
                      <TableCell width={38} />
                      <TableCell width={110}>Date</TableCell>
                      <TableCell width={150}>Voucher</TableCell>
                      <TableCell width={130}>Type / Source</TableCell>
                      <TableCell>Narration</TableCell>
                      <TableCell width={140} align="right">Debit</TableCell>
                      <TableCell width={140} align="right">Credit</TableCell>
                      <TableCell width={170} align="right">Running Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* opening */}
                    <TableRow sx={{ bgcolor: `${c.cyan}0d` }}>
                      <TableCell /><TableCell colSpan={4} sx={{ fontWeight: 700 }}>Opening Balance</TableCell>
                      <TableCell colSpan={2} />
                      <TableCell align="right"><Bal amount={data.openingBalance.amount} type={data.openingBalance.type} bold /></TableCell>
                    </TableRow>
                    {rows.length === 0 ? (
                      <TableRow><TableCell colSpan={8} sx={{ py: 5, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No transactions match the current filters.</Typography></TableCell></TableRow>
                    ) : rows.map((e) => {
                      const isOpen = !!expanded[e.id];
                      return (
                        <React.Fragment key={e.id}>
                          <TableRow hover sx={{ ...(e.reversal_entry_id ? { bgcolor: `${c.purple}0a` } : {}) }}>
                            <TableCell><IconButton size="small" onClick={() => setExpanded((s) => ({ ...s, [e.id]: !s[e.id] }))}>{isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(e.entry_date)}</TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography onClick={() => goVoucher(e)} sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8125rem', color: c.accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>{e.entry_number}</Typography>
                                {e.reversal_entry_id && <Chip label="Reversed" size="small" sx={{ height: 16, fontSize: '0.5625rem', bgcolor: `${c.purple}1f`, color: c.purple }} />}
                              </Stack>
                              {e.reference && <Typography variant="caption" color="text.secondary" display="block">Ref: {e.reference}</Typography>}
                            </TableCell>
                            <TableCell>
                              {e.journal_type ? <Typography variant="caption" sx={{ fontWeight: 700, color: typeTint(e.journal_type, c.text), display: 'block' }}>{e.journal_type}</Typography> : <Typography variant="caption" color="text.secondary">—</Typography>}
                              {e.source_type && e.source_type !== 'MANUAL'
                                ? <Chip label={String(e.source_type).replace(/_/g, ' ')} size="small" icon={<OpenInNew sx={{ fontSize: '12px !important' }} />} onClick={() => goVoucher(e)} sx={{ height: 18, fontSize: '0.5625rem', mt: 0.25, bgcolor: `${c.cyan}14`, color: c.cyan, '& .MuiChip-icon': { color: c.cyan } }} />
                                : <Typography variant="caption" color="text.secondary">Manual</Typography>}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 340 }}>
                              <Tooltip title={e.description || ''}><Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</Typography></Tooltip>
                              {e.line_description && e.line_description !== e.description && <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{e.line_description}</Typography>}
                            </TableCell>
                            <TableCell align="right"><DirAmt amount={e.debit_amount} dir="debit" /></TableCell>
                            <TableCell align="right"><DirAmt amount={e.credit_amount} dir="credit" /></TableCell>
                            <TableCell align="right"><Bal amount={e.running_balance} type={e.balance_type} /></TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 0, bgcolor: c.panel2 }}>
                                <Collapse in={isOpen}>
                                  <Box sx={{ p: 2 }}>
                                    <Grid container spacing={2}>
                                      {([['Cost Center', e.cost_center_name], ['Profit Center', e.profit_center_name], ['Project', e.project_name], ['Tax Code', e.tax_code], ['Vendor', e.vendor_name], ['Customer', e.customer_name], ['Company', e.company], ['Plant', e.plant], ['Posted By', e.posted_by_name], ['Created By', e.created_by_name]] as [string, any][])
                                        .filter(([, v]) => v).map(([l, v]) => (
                                          <Grid size={{ xs: 6, sm: 3 }} key={l}><Typography sx={labelSx}>{l}</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography></Grid>
                                        ))}
                                      {e.narration && <Grid size={12}><Typography sx={labelSx}>Narration</Typography><Typography variant="body2">{e.narration}</Typography></Grid>}
                                    </Grid>
                                    <Button size="small" endIcon={<ArrowForward />} sx={{ mt: 1.5 }} onClick={() => goVoucher(e)}>Open Journal 360</Button>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* closing */}
                    <TableRow sx={{ bgcolor: `${c.good}0d`, '& td': { borderBottom: 0, fontWeight: 700 } }}>
                      <TableCell /><TableCell colSpan={4}>Closing Balance</TableCell>
                      <TableCell align="right">{formatCurrency(data.totals.totalDebit)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.totals.totalCredit)}</TableCell>
                      <TableCell align="right"><Bal amount={data.closingBalance.amount} type={data.closingBalance.type} bold /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* --- account summary --- */}
            {tab === 1 && (
              <Box sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  {([['Account Code', acc.account_code], ['Account Name', acc.account_name], ['Account Type', acc.account_type], ['Parent Account', acc.parentName || '—'], ['Normal Balance', acc.normalBalance === 'Dr' ? 'Debit' : 'Credit'], ['Opening Balance', `${formatCurrency(data.openingBalance.amount)} ${data.openingBalance.type}`], ['Total Debit', formatCurrency(data.totals.totalDebit)], ['Total Credit', formatCurrency(data.totals.totalCredit)], ['Net Movement', `${formatCurrency(data.netMovement ?? 0)} ${data.netMovementType || ''}`], ['Closing Balance', `${formatCurrency(data.closingBalance.amount)} ${data.closingBalance.type}`], ['Transactions', String(data.count)], ['Last Transaction', data.lastTransactionDate ? fmtDate(data.lastTransactionDate) : '—'], ['Status', acc.isActive === false ? 'Inactive' : 'Active']] as [string, string][])
                    .map(([l, v]) => (<Grid size={{ xs: 6, sm: 4, md: 3 }} key={l}><Typography sx={labelSx}>{l}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{v}</Typography></Grid>))}
                </Grid>
              </Box>
            )}

            {/* --- analysis --- */}
            {tab === 2 && (
              <Box sx={{ p: 2.5 }}>
                {data.monthly && data.monthly.length ? (
                  <TableContainer>
                    <Table size="small" sx={{ '& td, & th': { borderColor: c.line } }}>
                      <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', color: c.sub } }}>
                        <TableCell>Month</TableCell><TableCell align="right">Debit</TableCell><TableCell align="right">Credit</TableCell><TableCell align="right">Net Movement</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {data.monthly.map((m) => (
                          <TableRow key={m.month}>
                            <TableCell sx={{ fontWeight: 600 }}>{new Date(Number(m.month.split('-')[0]), Number(m.month.split('-')[1]) - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</TableCell>
                            <TableCell align="right" sx={{ color: '#059669', fontVariantNumeric: 'tabular-nums' }}>{m.debit ? formatCurrency(m.debit) : '—'}</TableCell>
                            <TableCell align="right" sx={{ color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{m.credit ? formatCurrency(m.credit) : '—'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(Math.abs(m.net))} {acc.normalBalance === 'Dr' ? (m.net >= 0 ? 'Dr' : 'Cr') : (m.net >= 0 ? 'Cr' : 'Dr')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : <Typography variant="body2" color="text.secondary">No movement to analyse in this period.</Typography>}
              </Box>
            )}

            {/* --- insights & exceptions --- */}
            {tab === 3 && (
              <Box sx={{ p: 2.5 }}>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}><Lightbulb sx={{ fontSize: 18, color: c.accent }} /><Typography sx={{ fontWeight: 700 }}>Insights</Typography></Stack>
                    <Stack spacing={1}>
                      {insights.map((t, i) => <Stack key={i} direction="row" spacing={1} sx={{ p: 1.25, borderRadius: 2, border: line }}><Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.accent, mt: 0.75, flexShrink: 0 }} /><Typography variant="body2">{t}</Typography></Stack>)}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}><WarningAmberRounded sx={{ fontSize: 18, color: c.warn }} /><Typography sx={{ fontWeight: 700 }}>Exceptions</Typography></Stack>
                    {exceptions.length === 0 ? <Typography variant="body2" color="text.secondary">No exceptions detected for this account and period.</Typography> : (
                      <Stack spacing={1}>
                        {exceptions.map((ex, i) => (
                          <Box key={i} sx={{ p: 1.5, borderRadius: 2, border: line }}>
                            <StatusDot label={ex.title} tone={ex.tone} />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{ex.impact}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {filtered && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Showing {rows.length} of {data.count} transactions after advanced filters. Opening/closing balances reflect the full period.</Typography>}
        </Box>
      ) : null}

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const MoveNode: React.FC<{ c: any; label: string; node: React.ReactNode; highlight?: boolean }> = ({ c, label, node, highlight }) => (
  <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: highlight ? `${c.accent}12` : c.panel2, minWidth: 120 }}>
    <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.sub }}>{label}</Typography>
    <Box sx={{ mt: 0.25 }}>{node}</Box>
  </Box>
);

const DirAmt: React.FC<{ amount: number; dir: 'debit' | 'credit' }> = ({ amount, dir }) => {
  if (!amount) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const Icon = dir === 'debit' ? North : South;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
      <Icon sx={{ fontSize: 13, color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatAmount(amount)}</Typography>
    </Box>
  );
};

export default GeneralLedgerWorkspace;
